/* eslint-disable max-lines */
import { useEffect, useRef, useCallback, useMemo } from "react";
import { useMachine } from "@xstate/react";
import { useParams, useNavigate, useLocation } from "react-router";
import type { printOnDemand } from "common/utils/models";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useEditMode } from "@design-lab/shared/hooks/useEditMode";
import { useIsSample } from "@design-lab/shared/lib/config";
import { useAutoUploadDetection } from "@design-lab/widgets/design-lab-upload-artwork";
import {
  designLabInitMachine,
  designLabInitEvents,
  type DesignLabInitContext,
  type InitStage
} from "../design-lab-init.machine";
import { useProjectLoader } from "./useProjectLoader";
import { useProductsLoader } from "./useProductsLoader";
import { useFormRestoration } from "./useFormRestoration";
import { useWorkflowProcessor } from "./useWorkflowProcessor";

export type UseDesignLabInitReturn = {
  // Initialization state
  stage: InitStage;
  isInitialized: boolean;
  isLoading: boolean;
  error: Error | null;

  // Project data
  projectUuid: string;
  projectData: DesignLabInitContext["projectData"];
  savedProject: any; // Raw GraphQL response for backward compatibility

  // Products data
  productsMap: Map<string, any>;

  // Workflow state
  activeWorkflow: ReturnType<typeof useWorkflowProcessor>["activeWorkflow"];
  isProcessingWorkflow: boolean;

  // Modal controls
  isDesignLibraryOpen: boolean;
  isCanvaOpen: boolean;
  isAiDesignOpen: boolean;

  // Actions
  retryInit: () => void;
  reset: () => void;

  // Form restoration
  lastSavedDataRef: React.MutableRefObject<string | null>;
};

export function useDesignLabInit(): UseDesignLabInitReturn {
  const { uuid } = useParams<{ uuid?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentProduct } = useCurrentComposition();
  const { isEditMode, assemblyGroupId, compositionId } = useEditMode();
  const isSample = useIsSample();
  const hasRestoredRef = useRef(false);

  // Get URL flags from auto-upload detection
  const autoUploadData = useAutoUploadDetection();

  // Initialize state machine
  const [state, send] = useMachine(designLabInitMachine);

  // Use the raw URL flags context from autoUploadData, or default if not available
  const urlFlagsContext = useMemo(
    () =>
      autoUploadData.urlFlagsContext || {
        workflowFlags: {
          hasDesignLibrary: false,
          hasCanva: false,
          hasAiDesign: false,
          hasStartDesigning: false
        },
        consumedWorkflows: new Set<string>(),
        transientData: {
          artworkUrls: undefined,
          uploadUrls: undefined,
          fileContentType: undefined,
          catalogNumber: undefined,
          activeComposition: undefined,
          side: undefined,
          source: undefined,
          pdpQuantities: undefined,
          pdpSizeData: undefined,
          enhancementsStarted: undefined,
          dtfChannelUuid: undefined
        },
        isInitialized: false
      },
    [autoUploadData.urlFlagsContext]
  );

  // Determine current stage from state
  const getStage = (): InitStage => {
    if (state.matches("idle")) return "readingParams";
    if (state.matches("readingParams")) return "readingParams";
    if (state.matches("loadingProject")) return "loadingProject";
    if (state.matches("creatingProject")) return "creatingProject";
    if (state.matches("loadingProducts")) return "loadingProducts";
    if (state.matches("restoringData")) return "restoringData";
    if (state.matches("processingWorkflows")) return "processingWorkflows";
    if (state.matches("ready")) return "ready";
    if (state.matches("error")) return "error";
    if (state.matches("errorRecovery")) return "error";
    return "readingParams";
  };

  const stage = getStage();

  // Project loader - ONLY load when state machine is in loadingProject state
  const shouldLoadProject = state.matches("loadingProject");

  const {
    projectData,
    savedProject,
    isLoading: isLoadingProject,
    error: projectError,
    projectUuid: loadedProjectUuid
  } = useProjectLoader({
    uuid: uuid || "",
    isEditMode,
    assemblyGroupId: assemblyGroupId || "",
    isSample,
    shouldLoadProject,
    createNew: state.context.isNewProject
  });

  // Products loader - ONLY load when state machine says so
  // Never load in idle or readingParams states
  const isInitStarted = !state.matches("idle") && !state.matches("readingParams");
  const shouldLoadProducts =
    isInitStarted &&
    (state.matches("loadingProducts") ||
      state.matches("restoringData") ||
      state.matches("processingWorkflows") ||
      state.matches("ready"));

  const { productsMap, isLoadingProducts, allProductsLoaded } = useProductsLoader({
    catalogIds: state.context.catalogIds,
    isSample,
    hasDesignProject: Boolean(state.context.projectData || projectData), // Check both state and loaded data
    isLoadingProjectData: isLoadingProject,
    isEditMode,
    hasRestoredRef,
    hasCatalogFromUrl: Boolean(urlFlagsContext.transientData.catalogNumber),
    shouldLoad: shouldLoadProducts
  });

  // Form restoration
  const { restoreFromProject, lastSavedDataRef } = useFormRestoration({
    projectData: state.context.projectData || projectData || null, // Use projectData from loader as fallback
    currentProduct,
    compositionId,
    productsMap: (state.context.productsMap.size > 0
      ? state.context.productsMap
      : productsMap) as Map<string, printOnDemand.getProduct.Composition & { catalogId: string }>, // Use productsMap from loader as fallback
    shouldClearTargetSide:
      state.context.activeWorkflow === "designLibrary" ||
      state.context.activeWorkflow === "canva" ||
      state.context.activeWorkflow === "autoUpload",
    targetSide: state.context.targetSide,
    preserveExistingArtwork: false
  });

  // Workflow processor
  const {
    activeWorkflow,
    isProcessing: isProcessingWorkflow,
    isDesignLibraryOpen,
    isCanvaOpen,
    isAiDesignOpen
  } = useWorkflowProcessor({
    urlFlags: state.context.urlFlags,
    productsMap: state.context.productsMap as Map<
      string,
      printOnDemand.getProduct.Composition & { catalogId: string }
    >,
    productQueries: [], // This would come from products loader
    isReady: state.matches("processingWorkflows"),
    onWorkflowComplete: () => send(designLabInitEvents.workflowComplete()),
    onWorkflowError: error => send(designLabInitEvents.workflowError(error))
  });

  // Start initialization only after URL flags are ready
  const hasStartedRef = useRef(false);
  useEffect(() => {
    // Wait for URL flags to be initialized before starting
    // The url-flags.machine needs time to process the URL parameters
    if (state.matches("idle") && !hasStartedRef.current && urlFlagsContext.isInitialized) {
      hasStartedRef.current = true;
      // Start immediately, don't wait for anything
      send(designLabInitEvents.startInit(urlFlagsContext, isEditMode, isSample));
    }
  }, [state, isEditMode, isSample, send, uuid, urlFlagsContext]);

  // Handle project loading
  useEffect(() => {
    if (state.matches("loadingProject")) {
      if (projectData && !isLoadingProject) {
        send(designLabInitEvents.projectLoaded(projectData));
      } else if (projectError) {
        send(designLabInitEvents.projectError(projectError));
      } else if (!isLoadingProject && !projectData && !uuid) {
        send(designLabInitEvents.projectNotFound());
      }
    }
  }, [state, projectData, projectError, isLoadingProject, uuid, send]);

  // Handle project creation
  useEffect(() => {
    if (state.matches("creatingProject") && loadedProjectUuid) {
      send(designLabInitEvents.projectCreated(loadedProjectUuid));
    }
  }, [state, loadedProjectUuid, send]);

  // Handle products loading
  useEffect(() => {
    if (state.matches("loadingProducts") && allProductsLoaded && productsMap.size > 0) {
      send(designLabInitEvents.productsLoaded(productsMap));
    }
  }, [state, allProductsLoaded, productsMap, send]);

  // Handle form restoration
  const restorationInProgressRef = useRef(false);
  useEffect(() => {
    if (state.matches("restoringData")) {
      if (!hasRestoredRef.current && !restorationInProgressRef.current) {
        // Ensure we have the data needed for restoration
        const dataToRestore = state.context.projectData || projectData;
        const mapsToUse =
          state.context.productsMap.size > 0 ? state.context.productsMap : productsMap;

        if (!dataToRestore || mapsToUse.size === 0) {
          // Wait for data to be available
          return;
        }
        restorationInProgressRef.current = true;

        // Use setTimeout to ensure the promise completes in the next tick
        // This prevents issues with synchronous state updates
        setTimeout(() => {
          restoreFromProject()
            .then(() => {
              hasRestoredRef.current = true;
              restorationInProgressRef.current = false;
              send(designLabInitEvents.dataRestored());
            })
            .catch(error => {
              hasRestoredRef.current = false;
              restorationInProgressRef.current = false;
              send(designLabInitEvents.restorationError(error));
            });
        }, 0);
      }
    } else if (hasRestoredRef.current && restorationInProgressRef.current) {
      restorationInProgressRef.current = false;
    }
  }, [state, projectData, productsMap, restoreFromProject, send]);

  // Handle URL navigation updates
  useEffect(() => {
    const finalProjectUuid = state.context.projectUuid || loadedProjectUuid;
    if (finalProjectUuid && finalProjectUuid !== uuid) {
      const currentSearchParams = new URLSearchParams(location.search);
      const newPath = `/design-lab/${finalProjectUuid}?${currentSearchParams.toString()}`;
      navigate(newPath, { replace: true });
    }
  }, [state.context.projectUuid, loadedProjectUuid, uuid, navigate, location.search]);

  // Actions
  const retryInit = useCallback(() => {
    hasRestoredRef.current = false;
    send(designLabInitEvents.retryInit());
  }, [send]);

  const reset = useCallback(() => {
    hasRestoredRef.current = false;
    send(designLabInitEvents.reset());
  }, [send]);

  return {
    // Initialization state
    stage,
    isInitialized: state.matches("ready"),
    isLoading: isLoadingProject || isLoadingProducts || isProcessingWorkflow,
    error: state.context.error,

    // Project data
    projectUuid: state.context.projectUuid || loadedProjectUuid || uuid || "",
    projectData: state.context.projectData,
    savedProject, // Pass through raw GraphQL response

    // Products data
    productsMap: state.context.productsMap as Map<string, any>,

    // Workflow state
    activeWorkflow,
    isProcessingWorkflow,

    // Modal controls
    isDesignLibraryOpen,
    isCanvaOpen,
    isAiDesignOpen,

    // Actions
    retryInit,
    reset,

    // Form restoration
    lastSavedDataRef
  };
}
